import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { AISettings, MatchCriteria, MessageDraftSettings, AIUsageSummaryRow, AIUsageLog } from '@/types/ai';

export function useAISettings() {
  return useQuery({
    queryKey: ['ai-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', ['ai_settings', 'match_criteria', 'message_draft_settings']);

      if (error) throw error;

      const settings: Record<string, unknown> = {};
      for (const row of data || []) {
        settings[row.key] = row.value;
      }

      return {
        ai: settings.ai_settings as AISettings,
        matchCriteria: settings.match_criteria as MatchCriteria,
        messageDraft: settings.message_draft_settings as MessageDraftSettings,
      };
    },
  });
}

export function useUpdateAISetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('app_settings')
        .update({
          value: value as Record<string, unknown>,
          updated_by: user?.id || null,
          updated_at: new Date().toISOString(),
        })
        .eq('key', key);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
    },
  });
}

export function useAIUsageSummary(months: number = 3) {
  return useQuery({
    queryKey: ['ai-usage-summary', months],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('ai_usage_summary', { p_months: months });
      if (error) throw error;
      return (data || []) as AIUsageSummaryRow[];
    },
  });
}

export function useAIRecentLogs() {
  return useQuery({
    queryKey: ['ai-recent-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_usage_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Fetch actor names
      const actorIds = [...new Set((data || []).map((l: AIUsageLog) => l.actor_id).filter(Boolean))] as string[];
      let actorNames: Record<string, string> = {};

      if (actorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', actorIds);

        if (profiles) {
          actorNames = Object.fromEntries(profiles.map((p) => [p.user_id, p.full_name]));
        }
      }

      return (data || []).map((log: AIUsageLog) => ({
        ...log,
        actor_name: log.actor_id ? actorNames[log.actor_id] || null : null,
      }));
    },
  });
}
